import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { CheckCircle2, ArrowRight, ArrowLeft, Building2, Globe, Share2, Settings } from 'lucide-react';
import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const AgencyOnboarding = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const totalSteps = 4;

  // Step 1: Agency/Business Profile
  const [agencyProfile, setAgencyProfile] = useState({
    business_name: '',
    logo_url: '',
    website_url: '',
    primary_contact_name: '',
    contact_email: '',
    contact_phone: '',
    whatsapp_number: '',
    business_category: '',
    business_address: '',
    city: '',
    state: '',
    country: 'India',
    pincode: '',
    timezone: 'Asia/Kolkata'
  });

  // Step 2: SEO & Website Details
  const [seoWebsiteDetails, setSeoWebsiteDetails] = useState({
    main_website_url: '',
    cms_type: 'WordPress',
    cms_admin_url: '',
    cms_username: '',
    cms_password_encrypted: '',
    hosting_provider: '',
    hosting_cpanel_url: '',
    hosting_username: '',
    hosting_password_encrypted: '',
    ga4_measurement_id: '',
    gsc_property_url: '',
    gtm_container_id: '',
    target_countries: ['India'],
    target_cities: [],
    target_locations: [],
    target_keywords: [],
    competitor_urls: []
  });

  // Step 3: Social Profiles
  const [socialProfiles, setSocialProfiles] = useState({
    gbp_url: '',
    gbp_email: '',
    gbp_password_encrypted: '',
    facebook_page_url: '',
    instagram_url: '',
    instagram_handle: '',
    linkedin_company_page: '',
    twitter_handle: '',
    youtube_channel_url: '',
    other_social_links: []
  });

  // Step 4: Content Preferences
  const [contentPreferences, setContentPreferences] = useState({
    brand_tone: 'professional',
    preferred_languages: ['English'],
    blogs_per_month: 4,
    social_posts_per_week: 7,
    auto_publish_blogs: false,
    auto_publish_social: false,
    content_topics: [],
    avoid_topics: [],
    brand_voice_notes: ''
  });

  const getStepTitle = () => {
    const titles = [
      'Agency/Business Profile',
      'SEO & Website Details',
      'Social Media Profiles',
      'Content Preferences'
    ];
    return titles[currentStep - 1];
  };

  const handleNext = () => {
    if (currentStep < totalSteps) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleArrayInput = (value, setter, field) => {
    const array = value.split(',').map(item => item.trim()).filter(Boolean);
    setter(prev => ({ ...prev, [field]: array }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      const onboardingData = {
        agency_profile: agencyProfile,
        seo_website_details: seoWebsiteDetails,
        social_profiles: socialProfiles,
        content_preferences: contentPreferences
      };

      const response = await axios.post(
        `${API_BASE_URL}/api/onboarding`,
        onboardingData,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        toast.success('Onboarding completed! Welcome to AutomateSEO.ai');
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Onboarding error:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete onboarding');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 py-12 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-gray-900 mb-2">
            Complete Your Onboarding
          </h1>
          <p className="text-gray-600">
            Step {currentStep} of {totalSteps}: {getStepTitle()}
          </p>
        </div>

        {/* Progress Bar */}
        <div className="mb-8">
          <Progress value={(currentStep / totalSteps) * 100} className="h-2" />
        </div>

        {/* Step Content */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {currentStep === 1 && <Building2 className="w-6 h-6" />}
              {currentStep === 2 && <Globe className="w-6 h-6" />}
              {currentStep === 3 && <Share2 className="w-6 h-6" />}
              {currentStep === 4 && <Settings className="w-6 h-6" />}
              {getStepTitle()}
            </CardTitle>
            <CardDescription>
              {currentStep === 1 && 'Tell us about your business'}
              {currentStep === 2 && 'Your website and SEO information'}
              {currentStep === 3 && 'Connect your social media accounts'}
              {currentStep === 4 && 'Set your content preferences'}
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Step 1: Agency Profile */}
            {currentStep === 1 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Business Name *</Label>
                    <Input
                      value={agencyProfile.business_name}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, business_name: e.target.value })}
                      placeholder="Your Company Name"
                      required
                    />
                  </div>
                  <div>
                    <Label>Website URL *</Label>
                    <Input
                      value={agencyProfile.website_url}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, website_url: e.target.value })}
                      placeholder="https://example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Primary Contact Name *</Label>
                    <Input
                      value={agencyProfile.primary_contact_name}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, primary_contact_name: e.target.value })}
                      placeholder="John Doe"
                      required
                    />
                  </div>
                  <div>
                    <Label>Contact Email *</Label>
                    <Input
                      type="email"
                      value={agencyProfile.contact_email}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, contact_email: e.target.value })}
                      placeholder="contact@example.com"
                      required
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Contact Phone *</Label>
                    <Input
                      value={agencyProfile.contact_phone}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, contact_phone: e.target.value })}
                      placeholder="+91-9876543210"
                      required
                    />
                  </div>
                  <div>
                    <Label>WhatsApp Number</Label>
                    <Input
                      value={agencyProfile.whatsapp_number}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, whatsapp_number: e.target.value })}
                      placeholder="+91-9876543210"
                    />
                  </div>
                </div>

                <div>
                  <Label>Business Category *</Label>
                  <Select
                    value={agencyProfile.business_category}
                    onValueChange={(value) => setAgencyProfile({ ...agencyProfile, business_category: value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Healthcare">Healthcare</SelectItem>
                      <SelectItem value="E-commerce">E-commerce</SelectItem>
                      <SelectItem value="Technology">Technology</SelectItem>
                      <SelectItem value="Real Estate">Real Estate</SelectItem>
                      <SelectItem value="Education">Education</SelectItem>
                      <SelectItem value="Finance">Finance</SelectItem>
                      <SelectItem value="Hospitality">Hospitality</SelectItem>
                      <SelectItem value="Other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Business Address *</Label>
                  <Input
                    value={agencyProfile.business_address}
                    onChange={(e) => setAgencyProfile({ ...agencyProfile, business_address: e.target.value })}
                    placeholder="Street Address"
                    required
                  />
                </div>

                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>City *</Label>
                    <Input
                      value={agencyProfile.city}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, city: e.target.value })}
                      placeholder="City"
                      required
                    />
                  </div>
                  <div>
                    <Label>State *</Label>
                    <Input
                      value={agencyProfile.state}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, state: e.target.value })}
                      placeholder="State"
                      required
                    />
                  </div>
                  <div>
                    <Label>Pincode *</Label>
                    <Input
                      value={agencyProfile.pincode}
                      onChange={(e) => setAgencyProfile({ ...agencyProfile, pincode: e.target.value })}
                      placeholder="110001"
                      required
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Step 2: SEO & Website Details */}
            {currentStep === 2 && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Main Website URL *</Label>
                    <Input
                      value={seoWebsiteDetails.main_website_url}
                      onChange={(e) => setSeoWebsiteDetails({ ...seoWebsiteDetails, main_website_url: e.target.value })}
                      placeholder="https://yoursite.com"
                      required
                    />
                  </div>
                  <div>
                    <Label>CMS Type</Label>
                    <Select
                      value={seoWebsiteDetails.cms_type}
                      onValueChange={(value) => setSeoWebsiteDetails({ ...seoWebsiteDetails, cms_type: value })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="WordPress">WordPress</SelectItem>
                        <SelectItem value="Shopify">Shopify</SelectItem>
                        <SelectItem value="Custom">Custom</SelectItem>
                        <SelectItem value="Wix">Wix</SelectItem>
                        <SelectItem value="Webflow">Webflow</SelectItem>
                        <SelectItem value="Others">Others</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <div className="bg-blue-50 p-4 rounded-lg">
                  <p className="text-sm text-blue-800 mb-2 font-medium">CMS Credentials (Encrypted & Secure)</p>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label>CMS Admin URL</Label>
                      <Input
                        value={seoWebsiteDetails.cms_admin_url}
                        onChange={(e) => setSeoWebsiteDetails({ ...seoWebsiteDetails, cms_admin_url: e.target.value })}
                        placeholder="https://yoursite.com/wp-admin"
                      />
                    </div>
                    <div>
                      <Label>CMS Username</Label>
                      <Input
                        value={seoWebsiteDetails.cms_username}
                        onChange={(e) => setSeoWebsiteDetails({ ...seoWebsiteDetails, cms_username: e.target.value })}
                        placeholder="admin"
                      />
                    </div>
                    <div className="col-span-2">
                      <Label>CMS Password</Label>
                      <Input
                        type="password"
                        value={seoWebsiteDetails.cms_password_encrypted}
                        onChange={(e) => setSeoWebsiteDetails({ ...seoWebsiteDetails, cms_password_encrypted: e.target.value })}
                        placeholder="••••••••"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <Label>Google Analytics 4 Measurement ID</Label>
                  <Input
                    value={seoWebsiteDetails.ga4_measurement_id}
                    onChange={(e) => setSeoWebsiteDetails({ ...seoWebsiteDetails, ga4_measurement_id: e.target.value })}
                    placeholder="G-XXXXXXXXXX"
                  />
                </div>

                <div>
                  <Label>Google Search Console Property URL</Label>
                  <Input
                    value={seoWebsiteDetails.gsc_property_url}
                    onChange={(e) => setSeoWebsiteDetails({ ...seoWebsiteDetails, gsc_property_url: e.target.value })}
                    placeholder="https://yoursite.com"
                  />
                </div>

                <div>
                  <Label>Target Keywords (comma-separated)</Label>
                  <Textarea
                    value={seoWebsiteDetails.target_keywords.join(', ')}
                    onChange={(e) => handleArrayInput(e.target.value, setSeoWebsiteDetails, 'target_keywords')}
                    placeholder="seo services, digital marketing, web design"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Competitor URLs (comma-separated)</Label>
                  <Textarea
                    value={seoWebsiteDetails.competitor_urls.join(', ')}
                    onChange={(e) => handleArrayInput(e.target.value, setSeoWebsiteDetails, 'competitor_urls')}
                    placeholder="https://competitor1.com, https://competitor2.com"
                    rows={2}
                  />
                </div>
              </div>
            )}

            {/* Step 3: Social Profiles */}
            {currentStep === 3 && (
              <div className="space-y-4">
                <div>
                  <Label>Google Business Profile URL</Label>
                  <Input
                    value={socialProfiles.gbp_url}
                    onChange={(e) => setSocialProfiles({ ...socialProfiles, gbp_url: e.target.value })}
                    placeholder="https://g.page/your-business"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Facebook Page URL</Label>
                    <Input
                      value={socialProfiles.facebook_page_url}
                      onChange={(e) => setSocialProfiles({ ...socialProfiles, facebook_page_url: e.target.value })}
                      placeholder="https://facebook.com/yourpage"
                    />
                  </div>
                  <div>
                    <Label>Instagram URL</Label>
                    <Input
                      value={socialProfiles.instagram_url}
                      onChange={(e) => setSocialProfiles({ ...socialProfiles, instagram_url: e.target.value })}
                      placeholder="https://instagram.com/yourpage"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>LinkedIn Company Page</Label>
                    <Input
                      value={socialProfiles.linkedin_company_page}
                      onChange={(e) => setSocialProfiles({ ...socialProfiles, linkedin_company_page: e.target.value })}
                      placeholder="https://linkedin.com/company/yourcompany"
                    />
                  </div>
                  <div>
                    <Label>Twitter/X Handle</Label>
                    <Input
                      value={socialProfiles.twitter_handle}
                      onChange={(e) => setSocialProfiles({ ...socialProfiles, twitter_handle: e.target.value })}
                      placeholder="@yourhandle"
                    />
                  </div>
                </div>

                <div>
                  <Label>YouTube Channel URL</Label>
                  <Input
                    value={socialProfiles.youtube_channel_url}
                    onChange={(e) => setSocialProfiles({ ...socialProfiles, youtube_channel_url: e.target.value })}
                    placeholder="https://youtube.com/@yourchannel"
                  />
                </div>
              </div>
            )}

            {/* Step 4: Content Preferences */}
            {currentStep === 4 && (
              <div className="space-y-4">
                <div>
                  <Label>Brand Tone</Label>
                  <Select
                    value={contentPreferences.brand_tone}
                    onValueChange={(value) => setContentPreferences({ ...contentPreferences, brand_tone: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="friendly">Friendly</SelectItem>
                      <SelectItem value="educational">Educational</SelectItem>
                      <SelectItem value="promotional">Promotional</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Blogs per Month</Label>
                    <Input
                      type="number"
                      value={contentPreferences.blogs_per_month}
                      onChange={(e) => setContentPreferences({ ...contentPreferences, blogs_per_month: parseInt(e.target.value) })}
                      min="1"
                      max="30"
                    />
                  </div>
                  <div>
                    <Label>Social Posts per Week</Label>
                    <Input
                      type="number"
                      value={contentPreferences.social_posts_per_week}
                      onChange={(e) => setContentPreferences({ ...contentPreferences, social_posts_per_week: parseInt(e.target.value) })}
                      min="1"
                      max="30"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="auto_publish_blogs"
                      checked={contentPreferences.auto_publish_blogs}
                      onChange={(e) => setContentPreferences({ ...contentPreferences, auto_publish_blogs: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="auto_publish_blogs">Auto-publish Blogs</Label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="auto_publish_social"
                      checked={contentPreferences.auto_publish_social}
                      onChange={(e) => setContentPreferences({ ...contentPreferences, auto_publish_social: e.target.checked })}
                      className="w-4 h-4"
                    />
                    <Label htmlFor="auto_publish_social">Auto-publish Social Media Posts</Label>
                  </div>
                </div>

                <div>
                  <Label>Content Topics (comma-separated)</Label>
                  <Textarea
                    value={contentPreferences.content_topics.join(', ')}
                    onChange={(e) => handleArrayInput(e.target.value, setContentPreferences, 'content_topics')}
                    placeholder="technology, innovation, industry trends"
                    rows={2}
                  />
                </div>

                <div>
                  <Label>Brand Voice Guidelines (Optional)</Label>
                  <Textarea
                    value={contentPreferences.brand_voice_notes}
                    onChange={(e) => setContentPreferences({ ...contentPreferences, brand_voice_notes: e.target.value })}
                    placeholder="Describe your brand's unique voice and personality..."
                    rows={3}
                  />
                </div>
              </div>
            )}

            {/* Navigation Buttons */}
            <div className="flex justify-between pt-6 border-t">
              <Button
                variant="outline"
                onClick={handlePrevious}
                disabled={currentStep === 1 || loading}
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Previous
              </Button>

              {currentStep < totalSteps ? (
                <Button onClick={handleNext} disabled={loading}>
                  Next
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              ) : (
                <Button onClick={handleSubmit} disabled={loading}>
                  {loading ? 'Completing...' : 'Complete Onboarding'}
                  <CheckCircle2 className="w-4 h-4 ml-2" />
                </Button>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AgencyOnboarding;
